import React, { useCallback, useEffect, useRef, useState } from 'react';
import { followUpSummary, canToggle, fmtWhen } from '../utils/poFollowUp';

// "Vendor follow-up" box on the PO editor, under Confirmed by Supplier (2026-10-08).
// GET /purchase_orders/{id}/follow_up (asks MailQ live) and PUT .../follow_up to switch
// the automatic follow-up off or on for this PO. Rules live in jobq-api po_followups.py.
// `readOnly` (the phone's Purchase Orders page): no switch, the rest as it is.
export default function POFollowUpPanel({ apiBase, poId, refreshKey, readOnly = false }) {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  // The GET can wait on MailQ for seconds: a slower answer for the PO the user just
  // left must never land on the one they opened next.
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!poId) return;
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/purchase_orders/${poId}/follow_up`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (mine === seq.current && data && data.purchase_order_id === poId) setView(data);
    } catch (e) {
      if (mine === seq.current) setError("Couldn't load the follow-up status.");
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [apiBase, poId]);

  useEffect(() => { setView(null); setShowPreview(false); load(); }, [load, refreshKey]);

  async function toggle(on) {
    const mine = ++seq.current;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/purchase_orders/${poId}/follow_up`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto_follow_up: on }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (mine === seq.current && data && data.purchase_order_id === poId) setView(data);
    } catch (e) {
      if (mine === seq.current) setError("Couldn't change the automatic follow-up. Try again.");
    } finally {
      setSaving(false);
    }
  }

  if (!poId) return null;
  const s = followUpSummary(view);
  const responses = (view && view.mailq && view.mailq.responses) || [];
  const mentions = (view && view.mailq && view.mailq.mentions) || [];

  return (
    <div className="card mb-3" data-testid="po-follow-up">
      <div className="card-body py-2 px-3">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
          <strong className="small text-uppercase text-muted">Vendor follow-up</strong>
          {view && view.mode === 'shadow' && (
            <span className="badge bg-secondary" title="JobQ works out the follow-ups and logs them, but emails no vendor yet.">
              Shadow mode
            </span>
          )}
          {view && view.mode === 'off' && <span className="badge bg-secondary">Off</span>}
          {view && canToggle(view) && !readOnly && (
            <div className="form-check form-switch ms-auto mb-0">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={`autoFollowUp-${poId}`}
                checked={!!view.auto_follow_up}
                disabled={saving}
                onChange={(e) => toggle(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor={`autoFollowUp-${poId}`}>
                Follow up automatically after {view.business_days || 2} business days without a reply
              </label>
            </div>
          )}
        </div>

        {loading && !view && <div className="small text-muted">Checking MailQ…</div>}
        {error && <div className="small text-danger">{error}</div>}

        {view && (
          <>
            <div className={`small text-${s.tone === 'secondary' ? 'muted' : s.tone}`} data-testid="po-follow-up-text">
              {s.text}
            </div>
            {s.note && <div className="small text-muted">{s.note}</div>}
            {view.chased_email && (
              <div className="small text-muted">
                PO emailed {fmtWhen(view.chased_email.sent_at)} to {view.chased_email.to_email}
                {view.mailq_error && <> · <span className="text-warning">MailQ not reachable right now</span></>}
              </div>
            )}
            {responses.length > 0 && (
              <ul className="small mb-0 ps-3">
                {responses.map((m) => (
                  <li key={m.id}>
                    {m.mailq_url
                      ? <a href={m.mailq_url} target="_blank" rel="noreferrer">{m.subject || '(no subject)'}</a>
                      : (m.subject || '(no subject)')}
                    {' '}<span className="text-muted">· {m.from_address} · {fmtWhen(m.received_at)}
                      {m.status === 'spam' ? ' · in MailQ spam' : ''}</span>
                  </li>
                ))}
              </ul>
            )}
            {mentions.length > 0 && (
              <div className="small text-muted">
                Also mentioned by {mentions.map((m) => m.from_address).join(', ')} (another company, not counted as the vendor's reply).
              </div>
            )}
            {view.preview && !view.follow_up && (
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowPreview((v) => !v)}>
                {showPreview ? 'Hide the follow-up email' : 'Preview the follow-up email'}
              </button>
            )}
            {showPreview && view.preview && (
              <div className="border rounded p-2 mt-1 small" data-testid="po-follow-up-preview">
                <div><strong>To:</strong> {view.preview.to_email}</div>
                <div><strong>Subject:</strong> {view.preview.subject}</div>
                <pre className="mb-0 mt-1" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                  {view.preview.body_text}
                </pre>
                <div className="text-muted mt-1">A current copy of the PO PDF is attached. Sent as a reply to the PO email.</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
